import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import {
    BellSimpleIcon,
    CaretRightIcon,
    ClockIcon,
} from '@phosphor-icons/react';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { FullAlertRule } from '@/types';
import { SourcesPopover } from './SourcesPopover';
import { ActionsPopover } from './ActionsPopover';
import { useUser } from '@/hooks';
import { formatDate } from '@/utils/date';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { UserAvatar } from '@/components/NavigationBar';
import {
    InfoPanel,
    InfoPanelGroup,
    InfoPanelCard,
    InfoPanelTitle,
    InfoPanelRow,
    InfoPanelValue,
} from '@/components';

interface AlertInfoPanelProps {
    alertRule: FullAlertRule;
}

export function AlertRuleInfoPanel({ alertRule }: AlertInfoPanelProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const { user, isLoading: isUserLoading } = useUser();
    const [sourcesOpen, setSourcesOpen] = useState(false);
    const [actionsOpen, setActionsOpen] = useState(false);

    const actionsCount = alertRule.actions?.length ?? 0;
    const sourcesCount = alertRule.sources?.length || 0;

    return (
        <InfoPanel>
            <InfoPanelGroup>
                <Popover.Root open={sourcesOpen} onOpenChange={setSourcesOpen}>
                    <Popover.Trigger asChild>
                        <InfoPanelCard
                            as="button"
                            type="button"
                            isInteractive
                            isActive={sourcesOpen}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <InfoPanelTitle>
                                    {t('infoPanel.sources')}
                                </InfoPanelTitle>
                                <CaretRightIcon className="w-4 h-4 text-grey-300 shrink-0" />
                            </div>
                            <InfoPanelRow>
                                <SecurityCameraIconMirrored className="w-4 h-4 text-grey-300 shrink-0" />
                                <InfoPanelValue>
                                    {sourcesCount}{' '}
                                    {sourcesCount === 1
                                        ? t('infoPanel.source')
                                        : t('infoPanel.sources_plural')}
                                </InfoPanelValue>
                            </InfoPanelRow>
                        </InfoPanelCard>
                    </Popover.Trigger>
                    <Popover.Portal>
                        <Popover.Content
                            side="bottom"
                            align="start"
                            sideOffset={8}
                            className="z-50"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                            <SourcesPopover
                                alertRule={alertRule}
                                onClose={() => setSourcesOpen(false)}
                            />
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>

                <Popover.Root open={actionsOpen} onOpenChange={setActionsOpen}>
                    <Popover.Trigger asChild>
                        <InfoPanelCard
                            as="button"
                            type="button"
                            isInteractive
                            isActive={actionsOpen}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <InfoPanelTitle>
                                    {t('infoPanel.actions')}
                                </InfoPanelTitle>
                                <CaretRightIcon className="w-4 h-4 text-grey-300 shrink-0" />
                            </div>
                            <InfoPanelRow>
                                <BellSimpleIcon className="w-4 h-4 text-grey-300 shrink-0" />
                                <InfoPanelValue>
                                    {t('infoPanel.actionCount', {
                                        count: actionsCount,
                                    })}
                                </InfoPanelValue>
                            </InfoPanelRow>
                        </InfoPanelCard>
                    </Popover.Trigger>
                    <Popover.Portal>
                        <Popover.Content
                            side="bottom"
                            align="start"
                            sideOffset={8}
                            className="z-50"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                            <ActionsPopover
                                alertRule={alertRule}
                                onClose={() => setActionsOpen(false)}
                            />
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>
            </InfoPanelGroup>

            <InfoPanelGroup>
                <InfoPanelCard>
                    <InfoPanelTitle>{t('infoPanel.createdBy')}</InfoPanelTitle>
                    {isUserLoading ? (
                        <InfoPanelRow>
                            <div className="w-5 h-5 rounded-full bg-grey-600 animate-pulse shrink-0" />
                            <div className="h-5 w-24 bg-grey-600 rounded animate-pulse" />
                        </InfoPanelRow>
                    ) : (
                        <InfoPanelRow>
                            <UserAvatar
                                name={user?.name || t('infoPanel.unknownUser')}
                                size="sm"
                            />
                            <InfoPanelValue>
                                {user?.name || t('infoPanel.unknownUser')}
                            </InfoPanelValue>
                        </InfoPanelRow>
                    )}
                </InfoPanelCard>

                <InfoPanelCard>
                    <InfoPanelTitle>{t('infoPanel.createdAt')}</InfoPanelTitle>
                    <InfoPanelRow>
                        <ClockIcon className="w-4 h-4 text-grey-300 shrink-0" />
                        <InfoPanelValue>
                            {formatDate(alertRule.createdAt)}
                        </InfoPanelValue>
                    </InfoPanelRow>
                </InfoPanelCard>
            </InfoPanelGroup>
        </InfoPanel>
    );
}
